import { Controller } from "@hotwired/stimulus"

// A passkey or security key (WebAuthn, 11.10.2026): asks Central what the browser needs, lets the
// browser talk to the device, sends the answer back and follows where Central says to go.
//   data-controller="passkey" data-passkey-options-value="<url>" data-passkey-verify-value="<url>"
//   data-passkey-failed-value="..." data-passkey-unsupported-value="..."
//   a button with data-action="passkey#register" (a new key) or "passkey#signIn" (prove with one),
//   optional targets: name (the key's name), error (where a failure is said), button.
// Binary values travel as base64url, the way the server speaks them.
const toBytes = (text) => Uint8Array.from(atob(text.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0))
const toText = (buffer) => btoa(String.fromCharCode(...new Uint8Array(buffer))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")

export default class extends Controller {
  static values = { options: String, verify: String, failed: String, unsupported: String }
  static targets = ["name", "error", "button"]

  connect() {
    if (!window.PublicKeyCredential) this.say(this.unsupportedValue, true)
  }

  register(event) {
    event.preventDefault()
    this.run("create")
  }

  signIn(event) {
    event.preventDefault()
    this.run("get")
  }

  async run(kind) {
    this.say("")
    this.busy(true)
    try {
      const options = await this.post(this.optionsValue, {})
      if (!options) return
      options.challenge = toBytes(options.challenge)
      if (options.user) options.user.id = toBytes(options.user.id)
      for (const list of [options.excludeCredentials, options.allowCredentials]) (list || []).forEach((c) => { c.id = toBytes(c.id) })
      const credential = kind === "create" ? await navigator.credentials.create({ publicKey: options }) : await navigator.credentials.get({ publicKey: options })
      const answer = credential.response
      const response = kind === "create"
        ? { clientDataJSON: toText(answer.clientDataJSON), attestationObject: toText(answer.attestationObject), transports: answer.getTransports ? answer.getTransports() : [] }
        : { clientDataJSON: toText(answer.clientDataJSON), authenticatorData: toText(answer.authenticatorData), signature: toText(answer.signature),
            userHandle: answer.userHandle ? toText(answer.userHandle) : null }
      const body = { credential: { id: credential.id, rawId: toText(credential.rawId), type: credential.type,
                                   clientExtensionResults: credential.getClientExtensionResults ? credential.getClientExtensionResults() : {}, response } }
      if (this.hasNameTarget) body.name = this.nameTarget.value
      const result = await this.post(this.verifyValue, body)
      if (!result) return
      if (result.redirect) window.location.href = result.redirect
      else this.say(result.error || this.failedValue)
    } catch (_) {
      // Cancelled, timed out, or a key that is already registered: the browser says only "not allowed".
      this.say(this.failedValue)
    } finally {
      this.busy(false)
    }
  }

  // => the JSON answer, or null when the page is on its way somewhere else (signed out, confirm again).
  async post(url, body) {
    const response = await fetch(url, {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
      body: JSON.stringify(body)
    })
    if (response.redirected) { window.location.href = response.url; return null }
    const data = await response.json().catch(() => null)
    if (!response.ok && data?.redirect) { window.location.href = data.redirect; return null }
    return data || {}
  }

  say(text, lock = false) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = text
      this.errorTarget.hidden = !text
    }
    if (lock) this.buttonTargets.forEach((button) => { button.disabled = true })
  }

  busy(on) {
    if (window.PublicKeyCredential) this.buttonTargets.forEach((button) => { button.disabled = on })
  }
}
